/**
 * Step2_HazardIdentification Component
 *
 * Second step of the wizard - Identify all hazards associated with the task.
 *
 * @module components/Step2_HazardIdentification
 */

import React from 'react';
import {
  Plus,
  AlertTriangle,
  Lightbulb,
  Trash2,
  Users,
  GripVertical
} from 'lucide-react';

/**
 * Step2_HazardIdentification Component
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.setFormData - Function to update form data
 * @param {Object} props.errors - Validation errors
 */
export const Step2_HazardIdentification = ({ formData, setFormData, errors = {} }) => {

  const addHazard = () => {
    const newHazard = {
      id: `HAZ-${Date.now()}`,
      hazardDescription: '',
      whoAffected: '',
      initialLikelihood: 3,
      initialSeverity: 3,
      residualLikelihood: 2,
      residualSeverity: 2,
      existingControls: '',
      additionalControls: '',
      controlHierarchyLevel: 'administrative',
      actionRequired: '',
      actionOwner: '',
      actionDeadline: ''
    };

    setFormData(prev => ({
      ...prev,
      hazards: [...(prev.hazards || []), newHazard]
    }));
  };

  const updateHazard = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev.hazards.map((hazard, i) =>
        i === index ? { ...hazard, ...updates } : hazard
      )
    }));
  };

  const deleteHazard = (index) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev.hazards.filter((_, i) => i !== index)
    }));
  };

  const hazards = formData.hazards || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Identify Hazards</h3>
          <p className="text-sm text-gray-600 mt-1">
            List all potential hazards associated with this task
          </p>
        </div>
        <button
          onClick={addHazard}
          className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Hazard
        </button>
      </div>

      {/* Validation Error */}
      {errors.hazards && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {errors.hazards}
        </div>
      )}

      {/* Empty State */}
      {hazards.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No hazards identified yet</p>
          <p className="text-sm text-gray-500 mt-1">Click "Add Hazard" to begin</p>
          <button
            onClick={addHazard}
            className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add First Hazard
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {hazards.map((hazard, index) => (
            <HazardIdentificationCard
              key={hazard.id}
              hazard={hazard}
              index={index}
              onUpdate={(updates) => updateHazard(index, updates)}
              onDelete={() => deleteHazard(index)}
              error={errors[`hazard_${index}`]}
            />
          ))}
        </div>
      )}

      {/* Helpful guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Hazard Identification Tips</h4>
            <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
              <li>Think about what could go wrong during this task</li>
              <li>Consider environmental factors (weather, sea state, lighting)</li>
              <li>Include equipment failures and human error possibilities</li>
              <li>Don't forget slips, trips, and falls</li>
              <li>Consider both immediate and long-term health effects</li>
              <li>Ask experienced crew members for their input</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Common Hazard Categories Reference */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Common Maritime Hazard Categories</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            'Slips, trips & falls',
            'Moving/falling objects',
            'Working at height',
            'Confined spaces',
            'Manual handling',
            'Hazardous substances',
            'Electrical hazards',
            'Fire & explosion',
            'Noise exposure',
            'Weather conditions',
            'Vessel motion',
            'Fatigue'
          ].map((category) => (
            <div key={category} className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
              {category}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Individual Hazard Card for identification step
 */
const HazardIdentificationCard = ({ hazard, index, onUpdate, onDelete, error }) => {
  return (
    <div className={`rounded-xl border shadow-sm bg-white overflow-hidden ${
      error ? 'border-red-300' : 'border-gray-200'
    }`}>
      {/* Card Header */}
      <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GripVertical className="h-5 w-5 text-gray-400 cursor-grab" />
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <span className="font-medium text-gray-900">Hazard {index + 1}</span>
        </div>
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete hazard"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Card Content */}
      <div className="p-5 space-y-4">
        {/* Hazard Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hazard Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={hazard.hazardDescription || ''}
            onChange={e => onUpdate({ hazardDescription: e.target.value })}
            rows={2}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Describe the hazard (e.g., 'Crush injury from swinging loads during crane operations')"
          />
          {error && (
            <p className="mt-1 text-sm text-red-500">{error}</p>
          )}
        </div>

        {/* Who Could Be Affected */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="h-4 w-4 inline mr-1" />
            Who Could Be Affected
          </label>
          <input
            type="text"
            value={hazard.whoAffected || ''}
            onChange={e => onUpdate({ whoAffected: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            placeholder="e.g., Deck crew, crane operators, stevedores, visitors"
          />
          <p className="mt-1 text-xs text-gray-500">
            Consider direct workers, nearby personnel, contractors, and visitors
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step2_HazardIdentification;
